import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import axios from '../../../../api/axios';
import { TeleconsultationData } from '../types';

const FETCH_RETRY_DELAY_MS = 3000;
const FETCH_TIMEOUT_MS = 10000;

interface UseTeleconsultationReturn {
  teleconsultationData: TeleconsultationData | null;
  loadError: boolean;
  retryLoad: () => void;
  isExpired: boolean;
  isClosed: boolean;
  isEndedByDoctor: boolean;
  isEndedBySystem: boolean;
  showReminder: boolean;
  setShowReminder: (value: boolean) => void;
  showRating: boolean;
  setShowRating: (value: boolean) => void;
  setIsEndedByDoctor: (value: boolean) => void;
  setIsEndedBySystem: (value: boolean) => void;
}

/**
 * Handles fetching teleconsultation data and managing
 * consultation expiry timers and reminder state.
 */
export function useTeleconsultation(
  conversationID: string | undefined,
  decryptedToken: string
): UseTeleconsultationReturn {
  const navigate = useNavigate();

  const [teleconsultationData, setTeleconsultationData] =
    useState<TeleconsultationData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const retryLoadRef = useRef<() => void>(() => {});
  const [isExpired, setIsExpired] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [isEndedByDoctor, setIsEndedByDoctor] = useState(false);
  const [isEndedBySystem, setIsEndedBySystem] = useState(false);

  // ─── Guard: reminder hanya tampil sekali ──────────────────────────────────
  const reminderShownRef = useRef(false);
  const reminderApiCalledRef = useRef(false);

  // ─── Navigation error handler ─────────────────────────────────────────────

  const handleNavigationError = useCallback(
    (error: any) => {
      if (
        isAxiosError(error) &&
        (error.response?.status === 404 || error.response?.status === 400)
      ) {
        navigate('/not-found');
      } else if (error.status === 403) {
        console.log('You Are Not Authorized to access this resource!');
        navigate('/forbidden');
      } else {
        console.error('Unexpected error:', error);
      }
    },
    [navigate]
  );

  const fetchTeleconsultationData = useCallback(async (signal?: AbortSignal): Promise<boolean> => {
    if (!conversationID || !decryptedToken) return true;

    try {
      const response = await axios.get(`teleconsultation/${conversationID}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
        timeout: FETCH_TIMEOUT_MS,
        signal,
      });

      const data = response.data;

      sessionStorage.setItem('teleconsultationData', JSON.stringify(data));
      setTeleconsultationData(data);

      if (
        data.teleconsultation_end_time ||
        data.teleconsultation_status === 'resolve_by_doctor' ||
        data.teleconsultation_status === 'resolve_by_patient' ||
        data.teleconsultation_status === 'cancel_by_system'
      ) {
        setIsExpired(true);
        setIsClosed(true);

        if (data.teleconsultation_status === 'resolve_by_doctor') {
          setIsEndedByDoctor(true);
        }

        if (data.teleconsultation_status === 'cancel_by_system') {
          setIsEndedBySystem(true);
        }

        setShowRating(true);
      }
      return true;
    } catch (error) {
      console.error('Gagal mengambil data telekonsultasi:', error);
      handleNavigationError(error);
      const status = isAxiosError(error) ? error.response?.status : undefined;
      return status !== undefined && status < 500;
    }
  }, [conversationID, decryptedToken, handleNavigationError]);

  // ─── Fetch data ───────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    let loaded = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;

    const load = async () => {
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = null;
      controller?.abort();
      const current = new AbortController();
      controller = current;

      const done = await fetchTeleconsultationData(current.signal);
      if (cancelled || controller !== current) return;
      controller = null;

      setLoadError(!done);
      if (done) {
        loaded = true;
      } else {
        retryTimer = setTimeout(load, FETCH_RETRY_DELAY_MS);
      }
    };

    const handleOnline = () => {
      if (!loaded) load();
    };
    retryLoadRef.current = handleOnline;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') handleOnline();
    };

    load();
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      controller?.abort();
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchTeleconsultationData]);

  useEffect(() => {
    if (!conversationID || !decryptedToken) return;
    if (isClosed || isEndedByDoctor || isEndedBySystem) return;

    const interval = setInterval(() => {
      fetchTeleconsultationData();
    }, 180000); // ⏱️ 3 menit

    return () => clearInterval(interval);
  }, [
    fetchTeleconsultationData,
    conversationID,
    decryptedToken,
    isClosed,
    isEndedByDoctor,
    isEndedBySystem,
  ]);

  // ─── Expiry & reminder timers ─────────────────────────────────────────────

  useEffect(() => {
    if (!teleconsultationData?.teleconsultation_expired_at) return;

    // Langsung tampilkan rating jika sesi sudah berakhir sebelumnya
    if (
      teleconsultationData.teleconsultation_end_time &&
      !teleconsultationData.teleconsultation_isRating
    ) {
      setShowRating(true);
    }

    let expiredTime = teleconsultationData.teleconsultation_expired_at
      ? Date.parse(
          teleconsultationData.teleconsultation_expired_at.replace(' ', 'T') +
            '+07:00'
        )
      : null;

    const endTime = teleconsultationData.teleconsultation_end_time
      ? Date.parse(teleconsultationData.teleconsultation_end_time)
      : null;

    const timeOffset =
      new Date(teleconsultationData.server_time).getTime() - Date.now();
    const now = Date.now() + timeOffset;

    if (expiredTime === null) expiredTime = Date.now() + 10 * 60 * 1000;

    // Cek apakah sudah expired
    if (endTime && now > endTime) {
      setIsExpired(true);
      return;
    }
    if (now > expiredTime) {
      setIsExpired(true);
      return;
    }

    // Set timer expiry
    const expiredTimeout = setTimeout(
      () => setIsExpired(true),
      expiredTime - now
    );

    // Set timer reminder (5 menit sebelum expired)
    const delayReminder = expiredTime - 5 * 60 * 1000 - now;
    let reminderTimeout: NodeJS.Timeout | null = null;

    const fireReminder = () => {
      if (reminderShownRef.current) return;
      reminderShownRef.current = true;

      if (!reminderApiCalledRef.current && conversationID && decryptedToken) {
        reminderApiCalledRef.current = true;
        axios
          .post(
            `teleconsult/${conversationID}/reminder-5min`,
            {},
            { headers: { Authorization: `Bearer ${decryptedToken}` } }
          )
          .catch(() => {
            // silent — best effort
          });
      }

      setShowReminder(true);
    };

    if (delayReminder <= 0) {
      fireReminder();
    } else {
      reminderTimeout = setTimeout(fireReminder, delayReminder);
    }

    if (teleconsultationData.teleconsultation_end_time) {
      setShowRating(true);
    }

    return () => {
      clearTimeout(expiredTimeout);
      if (reminderTimeout) clearTimeout(reminderTimeout);
    };
  }, [teleconsultationData]);

  const retryLoad = useCallback(() => retryLoadRef.current(), []);

  return {
    teleconsultationData,
    loadError,
    retryLoad,
    isExpired,
    isClosed,
    isEndedByDoctor,
    isEndedBySystem,
    showReminder,
    setShowReminder,
    showRating,
    setShowRating,
    setIsEndedByDoctor,
    setIsEndedBySystem,
  };
}
